import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { CALL_RECORDS } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { CALL_RECORDS_LIST_PATH } from '../../navigations/path';
import {
  getLeadName,
  getLeadAssigneeNames,
  getStatusLabel,
  getRecordingsList,
  getRecordingUrl,
  getRecordingDisplayName,
} from './callRecordHelpers';

const ViewCallRecord = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!id) return;
    const fetchOne = async () => {
      setLoading(true);
      try {
        const { data: response } = await api.get(CALL_RECORDS.BY_ID(id), {
          params: {
            'relations[]': ['lead', 'closer', 'status', 'recordings', 'user'],
          },
        });
        const item = response?.data ?? response ?? {};
        setData(item);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load recording');
        navigate(CALL_RECORDS_LIST_PATH);
      } finally {
        setLoading(false);
      }
    };
    fetchOne();
  }, [id, navigate]);

  if (loading) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 3, display: 'flex', justifyContent: 'center', minHeight: '40vh' }}>
          <CircularProgress />
        </Container>
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout>
        <Container maxWidth="md" sx={{ py: 3 }}>
          <Typography color="error">Recording not found.</Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} sx={{ mt: 2 }} onClick={() => navigate(CALL_RECORDS_LIST_PATH)}>
            Back to list
          </Button>
        </Container>
      </PageLayout>
    );
  }

  const recordings = getRecordingsList(data);

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight={600}>
            Recording details
          </Typography>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(CALL_RECORDS_LIST_PATH)}>
            Back to list
          </Button>
        </Box>

        <Card variant="outlined">
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Lead
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {getLeadName(data)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Assignees
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {getLeadAssigneeNames(data)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip size="small" label={getStatusLabel(data)} variant="outlined" sx={{ mt: 0.5 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Note
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {data.note ?? '—'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Recordings
                </Typography>
                {recordings.length === 0 ? (
                  <Typography variant="body2">No recordings on this record.</Typography>
                ) : (
                  <Stack spacing={2} sx={{ maxWidth: 480 }}>
                    {recordings.map((rec, idx) => {
                      const recUrl = getRecordingUrl(rec);
                      const label = getRecordingDisplayName(rec);
                      const rk = rec?.id ?? rec?.uuid ?? idx;
                      return (
                        <Box
                          key={rk}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            pb: idx < recordings.length - 1 ? 2 : 0,
                            borderBottom: idx < recordings.length - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {recordings.length > 1 ? `${idx + 1}. ${label}` : label}
                          </Typography>
                          {recUrl ? (
                            <audio controls preload="metadata" src={recUrl} style={{ width: '100%' }}>
                              Your browser does not support audio.
                            </audio>
                          ) : (
                            <Typography variant="body2">No playable URL for this file.</Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewCallRecord;
